/**
 * Figma Node IDs:
 * Design Frame: 2416:1961
 * Instance IDs: I2081:1962, I2081:1964, I2081:1927, I2081:1929, I2449:3093, I2081:1934, I2081:1936, I2449:3141
 * Composite: true
 * Last Updated: 2024-12-23
 * Generated by: Claude Code figma-component command
 */
import React, { useId } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  Plus,
  Book,
  ChevronDown,
  Check,
  X,
  TriangleAlert,
  Info,
} from "@labxchange/icons";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { Label } from "@labxchange/ui-components/lib/Label";
import { Tooltip } from "@labxchange/ui-components/lib/Tooltip";

import {
  selectRootBorderlessClass,
  selectRootWithWidthClass,
  labelClass,
  selectContainerClass,
  selectStateVariants,
  selectBorderlessStateVariants,
  selectWithPrefixStateVariants,
  iconContainerClass,
  iconContainerBorderlessClass,
  iconStateVariants,
  helpTextStateVariants,
  prefixContainerClass,
  feedbackIconClass,
  hintIconClass,
  selectSmallStateVariants,
  selectWithPrefixSmallStateVariants,
  iconSmallStateVariants,
  iconContainerSmallClass,
  prefixContainerSmallClass,
} from "./Select.css";

const messages = defineMessages({
  defaultPlaceholder: {
    id: "Select.defaultPlaceholder",
    defaultMessage: "- Select -",
    description: "Default placeholder text shown when no option is selected",
  },
});

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectVariant = "default" | "borderless";
export type SelectSize = "default" | "small";
export type SelectWidth = "default" | "compact";

export interface SelectProps {
  label?: string;
  helpText?: string;
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  state?: "default" | "success" | "error" | "warning";
  errorMessage?: string;
  warningMessage?: string;
  onChange?: (value: string) => void;
  id?: string;
  name?: string;
  required?: boolean;
  ariaLabel?: string;
  prefix?: "plus" | "book";
  disabled?: boolean;
  variant?: SelectVariant;
  /** Size of the select input */
  size?: SelectSize;
  /** Width variant for the select component */
  width?: SelectWidth;
  /** Feedback text to show in tooltip when hovering over feedback icon. When provided with showFeedback, displays tooltip with success/fail variant */
  feedback?: string;
  /** Whether to show the feedback icon. When true without feedback, shows only the icon. When true with feedback, shows icon with tooltip */
  showFeedback?: boolean;
  /** Hint text to show in tooltip when hovering over hint icon (shown when no value) */
  hint?: string;
  /** Size of the hint icon (default: 18, use 24 for table cells) */
  hintIconSize?: number;
}

const PREFIX_ICONS = { plus: Plus, book: Book } as const;
const STATE_ICONS = {
  success: Check,
  error: X,
  warning: TriangleAlert,
} as const;

const FEEDBACK_TOOLTIP_VARIANT: Record<
  "success" | "error" | "warning",
  "success" | "fail" | "dark"
> = {
  success: "success",
  warning: "dark",
  error: "fail",
} as const;

export const Select: React.FC<SelectProps> = ({
  label,
  helpText,
  warningMessage,
  options,
  value,
  placeholder,
  errorMessage,
  onChange,
  id,
  name,
  ariaLabel,
  prefix,
  state = "default",
  required = false,
  disabled = false,
  variant = "default",
  size = "default",
  width = "default",
  feedback,
  showFeedback = false,
  hint,
  hintIconSize = 18,
}) => {
  const intl = useIntl();
  const generatedId = useId();
  const selectId = id || generatedId;

  const isBorderless = variant === "borderless";
  const isSmall = size === "small";
  const isError = state === "error";
  const isWarning = state === "warning";
  const hasValue = value !== undefined && value !== "";

  const selectState =
    state !== "default" ? state : hasValue ? "filled" : "default";
  const iconState =
    state === "success" || isError || isWarning
      ? state
      : disabled
        ? "disabled"
        : "default";

  const getSelectVariants = () => {
    if (isBorderless) {
      return selectBorderlessStateVariants;
    }
    if (isSmall) {
      return prefix
        ? selectWithPrefixSmallStateVariants
        : selectSmallStateVariants;
    }
    return prefix ? selectWithPrefixStateVariants : selectStateVariants;
  };

  const getIconVariants = () => {
    return isSmall ? iconSmallStateVariants : iconStateVariants;
  };

  let displayedText: string | undefined;
  if (isError && errorMessage) {
    displayedText = errorMessage;
  } else if (isWarning && warningMessage) {
    displayedText = warningMessage;
  } else {
    displayedText = helpText;
  }
  const textId = displayedText
    ? `${selectId}-${isError ? "error" : isWarning ? "warning" : "help"}`
    : undefined;

  const tooltipVariant: "success" | "fail" | "dark" =
    state === "success" || state === "error" || state === "warning"
      ? FEEDBACK_TOOLTIP_VARIANT[state]
      : "fail";

  const PrefixIcon = prefix ? PREFIX_ICONS[prefix] : null;
  const StateIcon =
    state === "success" || state === "error" || state === "warning"
      ? STATE_ICONS[state]
      : null;

  const rootClass = isBorderless
    ? selectRootBorderlessClass
    : selectRootWithWidthClass[width];
  const iconContainer = isBorderless
    ? iconContainerBorderlessClass
    : isSmall
      ? iconContainerSmallClass
      : iconContainerClass;
  const prefixContainer = isSmall
    ? prefixContainerSmallClass
    : prefixContainerClass;
  const selectVariants = getSelectVariants();
  const iconVariants = getIconVariants();
  const iconSize = isSmall ? 18 : 24;

  return (
    <div className={rootClass}>
      {label && !isBorderless && (
        <Label htmlFor={selectId} className={labelClass}>
          {label}
        </Label>
      )}
      <div className={selectContainerClass}>
        {PrefixIcon && !isBorderless && (
          <div className={prefixContainer} aria-hidden="true">
            <PrefixIcon className={iconVariants[iconState]} size={iconSize} />
          </div>
        )}
        <select
          id={selectId}
          name={name}
          value={value ?? ""}
          onChange={(e) => onChange && !disabled && onChange(e.target.value)}
          disabled={disabled}
          className={selectVariants[selectState]}
          aria-label={
            ariaLabel &&
            intl.formatMessage({
              id: ariaLabel,
              defaultMessage: placeholder,
              description: "Accessible label for the select input",
            })
          }
          aria-describedby={textId}
          aria-invalid={isError}
          aria-required={required}
          required={required}
        >
          <option value="" disabled>
            {placeholder
              ? intl.formatMessage({
                  id: placeholder,
                  defaultMessage: placeholder,
                  description: "Placeholder text for the select input",
                })
              : intl.formatMessage(messages.defaultPlaceholder)}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {intl.formatMessage({
                id: option.label,
                defaultMessage: option.label,
                description: "Label for a select option",
              })}
            </option>
          ))}
        </select>
        <div className={iconContainer}>
          {isBorderless ? (
            // In borderless mode, always reserve space to prevent layout shift
            hint && !hasValue ? (
              <Tooltip content={hint} variant="dark" placement="bottom">
                <span className={hintIconClass}>
                  <Info size={hintIconSize} />
                </span>
              </Tooltip>
            ) : StateIcon && showFeedback ? (
              feedback ? (
                <Tooltip
                  content={feedback}
                  variant={tooltipVariant}
                  placement="bottom"
                  showIcon
                >
                  <span className={feedbackIconClass}>
                    <StateIcon
                      className={iconVariants[iconState]}
                      size={iconSize}
                      aria-hidden="true"
                    />
                  </span>
                </Tooltip>
              ) : (
                <span className={feedbackIconClass}>
                  <StateIcon
                    className={iconVariants[iconState]}
                    size={iconSize}
                    aria-hidden="true"
                  />
                </span>
              )
            ) : null
          ) : (
            <>
              {StateIcon &&
                (showFeedback ? (
                  feedback ? (
                    <Tooltip
                      content={feedback}
                      variant={tooltipVariant}
                      placement="bottom"
                      showIcon
                    >
                      <span className={feedbackIconClass}>
                        <StateIcon
                          className={iconVariants[iconState]}
                          size={iconSize}
                          aria-hidden="true"
                        />
                      </span>
                    </Tooltip>
                  ) : (
                    <span className={feedbackIconClass}>
                      <StateIcon
                        className={iconVariants[iconState]}
                        size={iconSize}
                        aria-hidden="true"
                      />
                    </span>
                  )
                ) : (
                  <StateIcon
                    className={iconVariants[iconState]}
                    size={iconSize}
                    aria-hidden="true"
                  />
                ))}
              {hint && !hasValue && (
                <Tooltip content={hint} variant="dark" placement="bottom">
                  <span className={hintIconClass}>
                    <Info size={hintIconSize} />
                  </span>
                </Tooltip>
              )}
            </>
          )}
          {!disabled && (
            <ChevronDown
              className={iconVariants[iconState]}
              size={iconSize}
              aria-hidden="true"
            />
          )}
        </div>
      </div>
      {displayedText && !isBorderless && (
        <p
          id={textId}
          className={helpTextStateVariants[iconState]}
          role={isError || isWarning ? "alert" : undefined}
        >
          <RichText value={displayedText} />
        </p>
      )}
    </div>
  );
};
